import {
    CORRECTION_POINTS_FOREIGN,
    ERROR_REPORT_POINTS,
    FIRST_PLAN_POINTS,
    MINI_TEST_POINTS,
    POINTS_V4 as P,
    REFERRAL_BALANCE_PERCENT,
    REVIEW_POINTS,
    SUP_QUESTION_POINTS,
    WEEK_ON_PLAN_POINTS,
    type MeetingKind,
} from "@/shared/config/pointsRules";
import { isArabicUi } from "@/shared/config/arabicUi";
import { arPlural } from "@/shared/lib/plural/arabicPlural";
import { FRIEND_COURSE_PART_POINTS, HOW_TO_EXAMPLES } from "./howTo";
import type { SphereKey } from "./spheres";
import { pointsTexts } from "./texts";

/**
 * Окно «за что эти баллы» (PointsInfoRoot) — по нажатию на любую строку, где даются
 * баллы (владелец 03.10: «нажатие на любую строчку где даются баллы должно показывать
 * попап с объяснением что и как и там должна быть кнопка на страницу с объяснением всех
 * баллов этой сферы»).
 *
 * Чип говорит, ЧТО это за действие (data-points-action, data-points-ctx), окно — за что,
 * сколько и когда придут баллы. Тексты — по правилу владельца: коротко, «за что →
 * сколько», без денег за баллы, без порогов и годовых пределов; можно «один раз» и
 * «возврат снимает баллы». Все числа — из shared/config/pointsRules.ts (таблица v8.2);
 * чип с числом передаёт своё число, и окно повторяет ровно его.
 *
 * Языки — все языки сайта: ru, en, de, es, cn (zh → cn), ar — при включённом арабском
 * интерфейсе (иначе en, как весь /ar); остальные — en.
 */

export type PointsActionKey =
    | "ref.purchase"
    | "ref.giftClaim"
    | "ref.install"
    | "ref.started"
    | "ref.newAtMeeting"
    | "contrib.purchase"
    | "contrib.gift"
    | "contrib.review"
    | "contrib.correction"
    | "contrib.errorReport"
    | "contrib.testWorkout"
    | "work.weekOnPlan"
    | "work.firstPlan"
    | "work.newbie"
    | "social.meeting"
    | "social.organizer"
    | "social.goodDeed"
    | "social.addFriend"
    | "social.joinClub"
    | "social.firstMessage"
    | "learn.lesson"
    | "learn.lessonMinute"
    | "learn.course"
    | "learn.miniTest"
    | "learn.lessonQuestion"
    | "learn.sup"
    | "learn.minute"
    | "learn.specialist"
    | "learn.appLearning";

/** Сфера каждого действия: окно берёт её отсюда, а не только из data-sphere чипа. */
export const POINTS_ACTION_SPHERE: Record<PointsActionKey, SphereKey> = {
    "ref.purchase": "referrals",
    "ref.giftClaim": "referrals",
    "ref.install": "referrals",
    "ref.started": "referrals",
    "ref.newAtMeeting": "referrals",
    "contrib.purchase": "contrib",
    "contrib.gift": "contrib",
    "contrib.review": "contrib",
    "contrib.correction": "contrib",
    "contrib.errorReport": "contrib",
    "contrib.testWorkout": "contrib",
    "work.weekOnPlan": "workouts",
    "work.firstPlan": "workouts",
    "work.newbie": "workouts",
    "social.meeting": "social",
    "social.organizer": "social",
    "social.goodDeed": "social",
    "social.addFriend": "social",
    "social.joinClub": "social",
    "social.firstMessage": "social",
    "learn.lesson": "learning",
    "learn.lessonMinute": "learning",
    "learn.course": "learning",
    "learn.miniTest": "learning",
    "learn.lessonQuestion": "learning",
    "learn.sup": "learning",
    "learn.minute": "learning",
    "learn.specialist": "learning",
    "learn.appLearning": "learning",
};

export const POINTS_ACTION_KEYS = Object.keys(POINTS_ACTION_SPHERE) as PointsActionKey[];

export const isPointsActionKey = (v: string | null | undefined): v is PointsActionKey =>
    !!v && Object.prototype.hasOwnProperty.call(POINTS_ACTION_SPHERE, v);

/**
 * Что чип сообщает окну. `ctx` уточняет действие: у покупки — part | course | generic |
 * app | tokens | diagnostic, у встречи — её вид (seva, health, …). `amount` — число или
 * строка ровно как на чипе («+18 500», «до +4 450»).
 */
export type PointsInfo = { action: PointsActionKey; ctx?: string; amount?: number | string };

const MEETING_KINDS: MeetingKind[] = ["seva", "health", "spirit", "edu", "club", "online"];
const isMeetingKind = (v: string | undefined): v is MeetingKind => !!v && (MEETING_KINDS as string[]).includes(v);

/**
 * Сколько даёт действие, если чип своего числа не прислал: только постоянные числа
 * таблицы. У покупки, урока и уроков курса число зависит от продукта — его даёт чип.
 */
export const pointsActionAmount = (action: PointsActionKey, ctx?: string): number | null => {
    switch (action) {
        case "ref.purchase":
            if (ctx === "app") return HOW_TO_EXAMPLES.friendAppYear;
            if (ctx === "bundle") return HOW_TO_EXAMPLES.friendBundle;
            return FRIEND_COURSE_PART_POINTS;
        case "ref.giftClaim":
            return FRIEND_COURSE_PART_POINTS;
        case "ref.install":
            return P.referral.install;
        case "ref.started":
            return P.referral.friendStarted;
        case "ref.newAtMeeting":
            return P.referral.firstMeeting;
        case "contrib.purchase":
            if (ctx === "part" || ctx === "anyPart") return HOW_TO_EXAMPLES.coursePart;
            if (ctx === "app") return HOW_TO_EXAMPLES.appYear;
            if (ctx === "diagnostic") return HOW_TO_EXAMPLES.diagnostic;
            if (ctx === "bundle") return HOW_TO_EXAMPLES.bundle;
            if (ctx === "tokens") return HOW_TO_EXAMPLES.tokenPack;
            // Пакет «поиск + вопросы» на месяц (.com, человек на пакетах).
            if (ctx === "plan") return HOW_TO_EXAMPLES.planPack;
            return null;
        case "contrib.gift":
            return null;
        case "contrib.review":
            return REVIEW_POINTS;
        case "contrib.correction":
            return CORRECTION_POINTS_FOREIGN;
        case "contrib.errorReport":
            return ERROR_REPORT_POINTS;
        case "contrib.testWorkout":
            return P.workouts.testWorkout;
        case "work.weekOnPlan":
            return WEEK_ON_PLAN_POINTS;
        case "work.firstPlan":
            return FIRST_PLAN_POINTS;
        case "work.newbie":
            return P.workouts.newbieBonus;
        case "social.meeting":
            return isMeetingKind(ctx) ? P.meeting[ctx] : null;
        case "social.organizer":
            return P.meeting.perParticipant;
        case "social.goodDeed":
            return P.club.goodDeed;
        case "social.addFriend":
            return P.club.addFriend;
        case "social.joinClub":
            return P.club.joinClub;
        case "social.firstMessage":
            return P.club.firstMessage;
        case "learn.lesson":
        case "learn.course":
            return null;
        case "learn.lessonMinute":
            return P.learning.lessonMinute;
        case "learn.miniTest":
            return MINI_TEST_POINTS;
        case "learn.lessonQuestion":
            return P.learning.lessonQuestion;
        case "learn.sup":
            return SUP_QUESTION_POINTS;
        case "learn.minute":
            return P.learning.freeMinute;
        case "learn.specialist":
            return P.learning.specialistTicket;
        case "learn.appLearning":
            return P.learning.appLearning;
        default:
            return null;
    }
};

/** Текст одного действия. `title` по ctx: покупка части / курса, вид встречи. */
type ActionText = {
    title: string | Record<string, string>;
    how: string;
    /** Выгода самого человека (10 % покупки друга на баланс) — перед пометкой. */
    benefit?: string;
    /** «Вернёт покупку — баллы снимутся», «один раз за продукт». */
    note?: string;
};

export type PointsActionTexts = {
    /** «Все баллы: Приглашённые» — кнопка на экран «Как получить баллы» (знак › — в окне). */
    all: (sphere: string) => string;
    close: string;
    /** «баллов» после крупного числа. */
    unit: (n: number) => string;
    /** «до +4 450» — уроки и мини-тесты курса: столько, если пройти всё. */
    upTo: (amount: string) => string;
    /** Подписи окна без действия — по сфере (чип, который ещё не сказал, что он за действие). */
    about: Record<SphereKey, string>;
    actions: Record<PointsActionKey, ActionText>;
};

const B = REFERRAL_BALANCE_PERCENT;

/** 1 балл, 2 балла, 5 баллов. */
const ruUnit = (n: number): string => {
    const a = Math.abs(Math.round(n)) % 100;
    const b = a % 10;
    if (a > 10 && a < 20) return "баллов";
    if (b === 1) return "балл";
    if (b >= 2 && b <= 4) return "балла";
    return "баллов";
};

const RU: PointsActionTexts = {
    all: (s) => `Все баллы: ${s}`,
    close: "Понятно",
    unit: ruUnit,
    upTo: (a) => `до ${a}`,
    about: {
        contrib: "Ваш вклад в проект: покупки, отзывы и правки субтитров.",
        workouts: "Тренировки по вашему плану в приложении MY REVIVE.",
        social: "Встречи, клуб и добрые дела.",
        referrals: "Друзья, которые пришли по вашей ссылке.",
        learning: "Уроки курсов, мини-тесты и умный поиск.",
    },
    actions: {
        "ref.purchase": {
            title: {
                part: "Друг купит эту часть",
                course: "Друг купит этот курс",
                generic: "Друг купит часть курса",
                app: "Друг купит год приложения",
                bundle: "Друг купит пакет курсов",
            },
            how: "Баллы придут, когда друг оплатит покупку по вашей ссылке.",
            benefit: `А вам ещё — ${B} % его покупки на баланс.`,
            note: "Вернёт покупку — баллы снимутся.",
        },
        "ref.giftClaim": {
            title: "Друг заберёт подарок",
            how: "Баллы придут, когда друг заберёт ваш подарок.",
        },
        "ref.install": {
            title: "Друг установит приложение",
            how: "Баллы придут, когда друг установит MY REVIVE по вашей ссылке.",
        },
        "ref.started": {
            title: "Друг начнёт заниматься",
            how: "Баллы придут, когда друг начнёт тренироваться в MY REVIVE.",
        },
        "ref.newAtMeeting": {
            title: "Новый человек на встрече",
            how: "Баллы придут, когда человек впервые придёт на встречу и будет на её фото.",
        },
        "contrib.purchase": {
            title: {
                generic: "Покупка",
                part: "Покупка этой части",
                anyPart: "Покупка части курса",
                course: "Покупка курса",
                app: "Год приложения MY REVIVE",
                tokens: "Пакет токенов",
                plan: "Пакет поиска и вопросов",
                diagnostic: "Диагностика",
                bundle: "Пакет курсов",
            },
            how: "Баллы придут сразу после оплаты.",
            note: "Вернёте покупку — баллы снимутся.",
        },
        "contrib.gift": {
            title: "Подарок другу",
            how: "Баллы ваши — как за свою покупку.",
            benefit: "Друг заберёт подарок — вам ещё баллы, как за покупку друга.",
            note: "Вернёте покупку — баллы снимутся.",
        },
        "contrib.review": {
            title: "Отзыв о продукте",
            how: "Баллы придут, когда отправите отзыв, — с любой оценкой.",
            note: "Один раз за каждый продукт.",
        },
        "contrib.correction": {
            title: "Правка субтитров",
            how: "Баллы придут, когда правку проверят.",
        },
        "contrib.errorReport": {
            title: "Сообщение об ошибке",
            how: "Баллы придут, когда администратор подтвердит ошибку.",
        },
        "contrib.testWorkout": {
            title: "Тестовая тренировка",
            how: "Баллы придут, когда тренировку проверят.",
        },
        "work.weekOnPlan": {
            title: "Неделя по плану",
            how: "Делятся на тренировки недели: чем ближе к плану, тем больше.",
        },
        "work.firstPlan": {
            title: "Первый план",
            how: "Один раз — за самый первый план тренировок.",
        },
        "work.newbie": {
            title: "Новичку на полном плане",
            how: "Ещё за каждую неделю, пройденную по полному плану целиком.",
        },
        "social.meeting": {
            title: { generic: "Встреча" },
            how: "Каждому, кто на принятом фото встречи.",
        },
        "social.organizer": {
            title: "Организатору встречи",
            how: "За каждого, кто пришёл на вашу встречу, — сверх баллов самой встречи.",
        },
        "social.goodDeed": {
            title: "Доброе дело",
            how: "Отметьте сделанное — одно доброе дело в день.",
        },
        "social.addFriend": {
            title: "Новый друг",
            how: "Когда вы станете друзьями.",
        },
        "social.joinClub": {
            title: "Вступить в клуб",
            how: "Один раз — когда вступите в клуб.",
        },
        "social.firstMessage": {
            title: "Первое сообщение",
            how: "За первое сообщение в каждом чате.",
        },
        "learn.lesson": {
            title: "Урок курса",
            how: "Баллы придут, когда пройдёте урок. Один раз.",
        },
        "learn.lessonMinute": {
            title: "Минута урока",
            how: "За каждую минуту урока. Баллы придут, когда пройдёте урок.",
        },
        "learn.course": {
            title: "Уроки и мини-тесты",
            how: "Каждый урок и мини-тест — один раз.",
        },
        "learn.miniTest": {
            title: "Мини-тест",
            how: "Один раз за каждый тест.",
        },
        "learn.lessonQuestion": {
            title: "Вопрос по уроку",
            how: "Баллы придут, когда специалист ответит.",
        },
        "learn.sup": {
            title: "Умный поиск",
            how: "За каждый выбранный вопрос или поиск.",
        },
        "learn.minute": {
            title: "Лекция или клип",
            how: "За каждую просмотренную минуту.",
        },
        "learn.specialist": {
            title: "Обращение к специалисту",
            how: "Баллы придут, когда отправите обращение.",
        },
        "learn.appLearning": {
            title: "Гид или самодиагностика",
            how: "За гид или самодиагностику в приложении MY REVIVE.",
        },
    },
};

const EN: PointsActionTexts = {
    all: (s) => `All points: ${s}`,
    close: "Got it",
    unit: (n) => (Math.abs(n) === 1 ? "point" : "points"),
    upTo: (a) => `up to ${a}`,
    about: {
        contrib: "Your contribution: purchases, reviews and subtitle fixes.",
        workouts: "Workouts on your plan in the MY REVIVE app.",
        social: "Meetings, the club and good deeds.",
        referrals: "Friends who came through your link.",
        learning: "Course lessons, mini-tests and the smart search.",
    },
    actions: {
        "ref.purchase": {
            title: {
                part: "A friend buys this part",
                course: "A friend buys this course",
                generic: "A friend buys a course part",
                app: "A friend buys one year of the app",
                bundle: "A friend buys a course package",
            },
            how: "You get them when your friend pays through your link.",
            benefit: `Plus ${B}% of their purchase to your balance.`,
            note: "If they get a refund, the points go too.",
        },
        "ref.giftClaim": {
            title: "Your friend claims the gift",
            how: "You get them when your friend claims your gift.",
        },
        "ref.install": {
            title: "A friend installs the app",
            how: "You get them when a friend installs MY REVIVE through your link.",
        },
        "ref.started": {
            title: "A friend starts practising",
            how: "You get them when your friend starts working out in MY REVIVE.",
        },
        "ref.newAtMeeting": {
            title: "Someone new at a meeting",
            how: "You get them when someone comes to a meeting for the first time and is in its photo.",
        },
        "contrib.purchase": {
            title: {
                generic: "A purchase",
                part: "Buying this part",
                anyPart: "Buying a course part",
                course: "Buying a course",
                app: "A year of the MY REVIVE app",
                tokens: "A token pack",
                plan: "A search and questions pack",
                diagnostic: "A diagnostic",
                bundle: "A course package",
            },
            how: "You get them right after payment.",
            note: "A refund takes them back.",
        },
        "contrib.gift": {
            title: "A gift for a friend",
            how: "The points are yours, as for your own purchase.",
            benefit: "When your friend claims the gift, you also get points, as for a friend's purchase.",
            note: "A refund takes them back.",
        },
        "contrib.review": {
            title: "A product review",
            how: "You get them when you send a review — any rating.",
            note: "Once per product.",
        },
        "contrib.correction": {
            title: "A subtitle fix",
            how: "You get them once the fix is checked.",
        },
        "contrib.errorReport": {
            title: "An error report",
            how: "You get them once an administrator confirms the error.",
        },
        "contrib.testWorkout": {
            title: "A test workout",
            how: "You get them once the workout is checked.",
        },
        "work.weekOnPlan": {
            title: "A week on plan",
            how: "Shared across the week's workouts: the closer to the plan, the more.",
        },
        "work.firstPlan": {
            title: "Your first plan",
            how: "Once — for your very first workout plan.",
        },
        "work.newbie": {
            title: "A newcomer on the full plan",
            how: "Extra for every week done fully on the full plan.",
        },
        "social.meeting": {
            title: { generic: "A meeting" },
            how: "For everyone in the meeting's accepted photo.",
        },
        "social.organizer": {
            title: "For the organizer",
            how: "For everyone who comes to your meeting — on top of the meeting itself.",
        },
        "social.goodDeed": {
            title: "A good deed",
            how: "Mark it done — one good deed a day.",
        },
        "social.addFriend": {
            title: "A new friend",
            how: "When you become friends.",
        },
        "social.joinClub": {
            title: "Join a club",
            how: "Once — when you join a club.",
        },
        "social.firstMessage": {
            title: "First message",
            how: "For your first message in each chat.",
        },
        "learn.lesson": {
            title: "A course lesson",
            how: "You get them when you finish the lesson — once per lesson.",
        },
        "learn.lessonMinute": {
            title: "A lesson minute",
            how: "For every minute of a lesson. You get them when you finish the lesson.",
        },
        "learn.course": {
            title: "Lessons and mini-tests",
            how: "Each lesson and mini-test counts once.",
        },
        "learn.miniTest": {
            title: "A mini-test",
            how: "Once per test.",
        },
        "learn.lessonQuestion": {
            title: "A lesson question",
            how: "You get them when a specialist answers.",
        },
        "learn.sup": {
            title: "Smart search",
            how: "For each question you choose or search you run.",
        },
        "learn.minute": {
            title: "A lecture or clip",
            how: "For every minute you watch.",
        },
        "learn.specialist": {
            title: "A request to a specialist",
            how: "You get them when you send the request.",
        },
        "learn.appLearning": {
            title: "A guide or self-diagnostics",
            how: "For a guide or self-diagnostics in the MY REVIVE app.",
        },
    },
};

const DE: PointsActionTexts = {
    all: (s) => `Alle Punkte: ${s}`,
    close: "Verstanden",
    unit: (n) => (Math.abs(n) === 1 ? "Punkt" : "Punkte"),
    upTo: (a) => `bis zu ${a}`,
    about: {
        contrib: "Ihr Beitrag zum Projekt: Käufe, Bewertungen und Untertitel-Korrekturen.",
        workouts: "Training nach Ihrem Plan in der App MY REVIVE.",
        social: "Treffen, der Club und gute Taten.",
        referrals: "Freunde, die über Ihren Link gekommen sind.",
        learning: "Kurslektionen, Mini-Tests und die intelligente Suche.",
    },
    actions: {
        "ref.purchase": {
            title: {
                part: "Ihr Freund kauft diesen Teil",
                course: "Ihr Freund kauft diesen Kurs",
                generic: "Ein Freund kauft einen Kursteil",
                app: "Ein Freund kauft ein Jahr der App",
                bundle: "Ein Freund kauft ein Kurspaket",
            },
            how: "Sie erhalten sie, sobald Ihr Freund über Ihren Link bezahlt.",
            benefit: `Dazu ${B} % seines Kaufs als Guthaben.`,
            note: "Bei einer Rückerstattung werden sie abgezogen.",
        },
        "ref.giftClaim": {
            title: "Ihr Freund löst das Geschenk ein",
            how: "Sie erhalten sie, sobald Ihr Freund Ihr Geschenk einlöst.",
        },
        "ref.install": {
            title: "Ein Freund installiert die App",
            how: "Sie erhalten sie, wenn ein Freund MY REVIVE über Ihren Link installiert.",
        },
        "ref.started": {
            title: "Ein Freund beginnt zu üben",
            how: "Sie erhalten sie, wenn Ihr Freund in MY REVIVE zu trainieren beginnt.",
        },
        "ref.newAtMeeting": {
            title: "Jemand Neues beim Treffen",
            how: "Sie erhalten sie, wenn jemand zum ersten Mal zu einem Treffen kommt und auf dem Foto ist.",
        },
        "contrib.purchase": {
            title: {
                generic: "Ein Kauf",
                part: "Kauf dieses Teils",
                anyPart: "Kauf eines Kursteils",
                course: "Kauf eines Kurses",
                app: "Ein Jahr der App MY REVIVE",
                tokens: "Ein Token-Paket",
                plan: "Paket Suche und Fragen",
                diagnostic: "Eine Diagnostik",
                bundle: "Ein Kurspaket",
            },
            how: "Sie erhalten sie direkt nach der Zahlung.",
            note: "Bei einer Rückerstattung werden sie abgezogen.",
        },
        "contrib.gift": {
            title: "Ein Geschenk für einen Freund",
            how: "Die Punkte gehören Ihnen – wie bei einem eigenen Kauf.",
            benefit: "Löst Ihr Freund das Geschenk ein, erhalten Sie zusätzlich Punkte wie für den Kauf eines Freundes.",
            note: "Bei einer Rückerstattung werden sie abgezogen.",
        },
        "contrib.review": {
            title: "Eine Produktbewertung",
            how: "Sie erhalten sie, sobald Sie die Bewertung senden – mit beliebiger Wertung.",
            note: "Einmal pro Produkt.",
        },
        "contrib.correction": {
            title: "Eine Untertitel-Korrektur",
            how: "Sie erhalten sie, sobald die Korrektur geprüft ist.",
        },
        "contrib.errorReport": {
            title: "Eine Fehlermeldung",
            how: "Sie erhalten sie, sobald ein Administrator den Fehler bestätigt.",
        },
        "contrib.testWorkout": {
            title: "Ein Testtraining",
            how: "Sie erhalten sie, sobald das Training geprüft ist.",
        },
        "work.weekOnPlan": {
            title: "Eine Woche nach Plan",
            how: "Verteilt auf die Trainings der Woche: je näher am Plan, desto mehr.",
        },
        "work.firstPlan": {
            title: "Ihr erster Plan",
            how: "Einmalig – für Ihren allerersten Trainingsplan.",
        },
        "work.newbie": {
            title: "Neuling mit vollem Plan",
            how: "Zusätzlich für jede Woche, die ganz nach dem vollen Plan erfüllt ist.",
        },
        "social.meeting": {
            title: { generic: "Ein Treffen" },
            how: "Für alle auf dem angenommenen Foto des Treffens.",
        },
        "social.organizer": {
            title: "Für den Organisator",
            how: "Für jeden, der zu Ihrem Treffen kommt – zusätzlich zum Treffen selbst.",
        },
        "social.goodDeed": {
            title: "Eine gute Tat",
            how: "Als erledigt markieren – eine gute Tat pro Tag.",
        },
        "social.addFriend": {
            title: "Ein neuer Freund",
            how: "Wenn Sie Freunde werden.",
        },
        "social.joinClub": {
            title: "Einem Club beitreten",
            how: "Einmalig – wenn Sie einem Club beitreten.",
        },
        "social.firstMessage": {
            title: "Erste Nachricht",
            how: "Für Ihre erste Nachricht in jedem Chat.",
        },
        "learn.lesson": {
            title: "Eine Kurslektion",
            how: "Sie erhalten sie, sobald die Lektion abgeschlossen ist. Nur einmal.",
        },
        "learn.lessonMinute": {
            title: "Eine Lektionsminute",
            how: "Für jede Minute einer Lektion. Sie erhalten sie, sobald Sie die Lektion abschließen.",
        },
        "learn.course": {
            title: "Lektionen und Mini-Tests",
            how: "Jede Lektion und jeder Mini-Test zählt einmal.",
        },
        "learn.miniTest": {
            title: "Ein Mini-Test",
            how: "Einmal pro Test.",
        },
        "learn.lessonQuestion": {
            title: "Eine Frage zur Lektion",
            how: "Sie erhalten sie, sobald ein Spezialist antwortet.",
        },
        "learn.sup": {
            title: "Intelligente Suche",
            how: "Für jede gewählte Frage oder Suche.",
        },
        "learn.minute": {
            title: "Ein Vortrag oder Clip",
            how: "Für jede angesehene Minute.",
        },
        "learn.specialist": {
            title: "Anfrage an einen Spezialisten",
            how: "Sie erhalten sie, sobald Sie die Anfrage senden.",
        },
        "learn.appLearning": {
            title: "Ein Leitfaden oder Selbstdiagnose",
            how: "Für einen Leitfaden oder eine Selbstdiagnose in der App MY REVIVE.",
        },
    },
};

const ES: PointsActionTexts = {
    all: (s) => `Todos los puntos: ${s}`,
    close: "Entendido",
    unit: (n) => (Math.abs(n) === 1 ? "punto" : "puntos"),
    upTo: (a) => `hasta ${a}`,
    about: {
        contrib: "Tu aporte al proyecto: compras, reseñas y correcciones de subtítulos.",
        workouts: "Entrenamientos según tu plan en la app MY REVIVE.",
        social: "Encuentros, el club y buenas acciones.",
        referrals: "Amigos que llegaron con tu enlace.",
        learning: "Lecciones de cursos, minitests y la búsqueda inteligente.",
    },
    actions: {
        "ref.purchase": {
            title: {
                part: "Tu amigo compra esta parte",
                course: "Tu amigo compra este curso",
                generic: "Un amigo compra una parte del curso",
                app: "Un amigo compra un año de la app",
                bundle: "Un amigo compra un paquete de cursos",
            },
            how: "Los recibes cuando tu amigo paga con tu enlace.",
            benefit: `Y además, el ${B} % de su compra en tu saldo.`,
            note: "Si pide un reembolso, se retiran.",
        },
        "ref.giftClaim": {
            title: "Tu amigo recibe el regalo",
            how: "Los recibes cuando tu amigo reciba tu regalo.",
        },
        "ref.install": {
            title: "Un amigo instala la app",
            how: "Los recibes cuando un amigo instala MY REVIVE con tu enlace.",
        },
        "ref.started": {
            title: "Un amigo empieza a practicar",
            how: "Los recibes cuando tu amigo empieza a entrenar en MY REVIVE.",
        },
        "ref.newAtMeeting": {
            title: "Alguien nuevo en un encuentro",
            how: "Los recibes cuando alguien viene por primera vez a un encuentro y sale en la foto.",
        },
        "contrib.purchase": {
            title: {
                generic: "Una compra",
                part: "Comprar esta parte",
                anyPart: "Comprar una parte del curso",
                course: "Comprar un curso",
                app: "Un año de la app MY REVIVE",
                tokens: "Un paquete de tokens",
                plan: "Paquete de búsqueda y preguntas",
                diagnostic: "Un diagnóstico",
                bundle: "Un paquete de cursos",
            },
            how: "Los recibes justo después del pago.",
            note: "Si pides un reembolso, se retiran.",
        },
        "contrib.gift": {
            title: "Un regalo para un amigo",
            how: "Los puntos son tuyos, como en tu propia compra.",
            benefit: "Cuando tu amigo reciba el regalo, recibes además puntos, como por la compra de un amigo.",
            note: "Si pides un reembolso, se retiran.",
        },
        "contrib.review": {
            title: "Una reseña de producto",
            how: "Los recibes al enviar tu reseña, con cualquier valoración.",
            note: "Una vez por producto.",
        },
        "contrib.correction": {
            title: "Una corrección de subtítulos",
            how: "Los recibes cuando se revisa la corrección.",
        },
        "contrib.errorReport": {
            title: "Un aviso de error",
            how: "Los recibes cuando un administrador confirma el error.",
        },
        "contrib.testWorkout": {
            title: "Un entrenamiento de prueba",
            how: "Los recibes cuando se revisa el entrenamiento.",
        },
        "work.weekOnPlan": {
            title: "Una semana según el plan",
            how: "Se reparten entre los entrenamientos de la semana: cuanto más cerca del plan, más.",
        },
        "work.firstPlan": {
            title: "Tu primer plan",
            how: "Una vez: por tu primer plan de entrenamiento.",
        },
        "work.newbie": {
            title: "Principiante con el plan completo",
            how: "Extra por cada semana cumplida entera con el plan completo.",
        },
        "social.meeting": {
            title: { generic: "Un encuentro" },
            how: "Para cada persona en la foto aceptada del encuentro.",
        },
        "social.organizer": {
            title: "Para quien organiza",
            how: "Por cada persona que venga a tu encuentro, además del propio encuentro.",
        },
        "social.goodDeed": {
            title: "Una buena acción",
            how: "Márcala como hecha: una buena acción al día.",
        },
        "social.addFriend": {
            title: "Un nuevo amigo",
            how: "Cuando sean amigos.",
        },
        "social.joinClub": {
            title: "Unirte a un club",
            how: "Una vez: al unirte a un club.",
        },
        "social.firstMessage": {
            title: "Primer mensaje",
            how: "Por tu primer mensaje en cada chat.",
        },
        "learn.lesson": {
            title: "Una lección del curso",
            how: "Los recibes al completar la lección. Solo una vez.",
        },
        "learn.lessonMinute": {
            title: "Un minuto de lección",
            how: "Por cada minuto de lección. Los recibes al completar la lección.",
        },
        "learn.course": {
            title: "Lecciones y minitests",
            how: "Cada lección y minitest cuenta una vez.",
        },
        "learn.miniTest": {
            title: "Un minitest",
            how: "Una vez por test.",
        },
        "learn.lessonQuestion": {
            title: "Una pregunta sobre la lección",
            how: "Los recibes cuando responda un especialista.",
        },
        "learn.sup": {
            title: "Búsqueda inteligente",
            how: "Por cada pregunta que elijas o búsqueda que hagas.",
        },
        "learn.minute": {
            title: "Una conferencia o clip",
            how: "Por cada minuto que veas.",
        },
        "learn.specialist": {
            title: "Consulta a un especialista",
            how: "Los recibes al enviar la consulta.",
        },
        "learn.appLearning": {
            title: "Una guía o autodiagnóstico",
            how: "Por una guía o un autodiagnóstico en la app MY REVIVE.",
        },
    },
};

const CN: PointsActionTexts = {
    all: (s) => `全部积分：${s}`,
    close: "知道了",
    unit: () => "积分",
    upTo: (a) => `最多 ${a}`,
    about: {
        contrib: "您对项目的贡献：购买、评价和字幕修正。",
        workouts: "在 MY REVIVE 应用中按计划训练。",
        social: "聚会、俱乐部和善行。",
        referrals: "通过您的链接来的朋友。",
        learning: "课程课时、小测验和智能搜索。",
    },
    actions: {
        "ref.purchase": {
            title: {
                part: "朋友购买这一部分",
                course: "朋友购买这门课程",
                generic: "朋友购买课程的一部分",
                app: "朋友购买一年应用",
                bundle: "朋友购买课程套餐",
            },
            how: "朋友通过您的链接付款后到账。",
            benefit: `另外，其购买额的 ${B}% 计入您的余额。`,
            note: "如果退款，积分将被扣回。",
        },
        "ref.giftClaim": {
            title: "朋友领取礼物",
            how: "朋友领取您的礼物后到账。",
        },
        "ref.install": {
            title: "朋友安装应用",
            how: "朋友通过您的链接安装 MY REVIVE 后到账。",
        },
        "ref.started": {
            title: "朋友开始练习",
            how: "朋友在 MY REVIVE 开始训练后到账。",
        },
        "ref.newAtMeeting": {
            title: "聚会来了新人",
            how: "有人首次参加聚会并出现在照片中后到账。",
        },
        "contrib.purchase": {
            title: {
                generic: "购买",
                part: "购买这一部分",
                anyPart: "购买课程的一部分",
                course: "购买课程",
                app: "MY REVIVE 应用一年",
                tokens: "代币包",
                plan: "搜索和提问套餐",
                diagnostic: "诊断",
                bundle: "课程套餐",
            },
            how: "付款后立即到账。",
            note: "退款后积分将被扣回。",
        },
        "contrib.gift": {
            title: "送给朋友的礼物",
            how: "积分归您，与自己购买相同。",
            benefit: "朋友领取礼物后，您还将获得积分，与朋友购买相同。",
            note: "退款后积分将被扣回。",
        },
        "contrib.review": {
            title: "产品评价",
            how: "提交评价后到账，任何评分都可以。",
            note: "每个产品一次。",
        },
        "contrib.correction": {
            title: "字幕修正",
            how: "修正审核通过后到账。",
        },
        "contrib.errorReport": {
            title: "错误报告",
            how: "管理员确认错误后到账。",
        },
        "contrib.testWorkout": {
            title: "测试训练",
            how: "训练审核通过后到账。",
        },
        "work.weekOnPlan": {
            title: "按计划完成一周",
            how: "分配到本周各次训练：越接近计划，积分越多。",
        },
        "work.firstPlan": {
            title: "第一个计划",
            how: "仅一次——制定第一个训练计划时。",
        },
        "work.newbie": {
            title: "新手完成完整计划",
            how: "每完整完成一周完整计划，额外获得。",
        },
        "social.meeting": {
            title: { generic: "聚会" },
            how: "聚会照片审核通过后，照片中的每个人都可获得。",
        },
        "social.organizer": {
            title: "组织者",
            how: "每有一人参加您的聚会即可获得，另加聚会本身的积分。",
        },
        "social.goodDeed": {
            title: "善行",
            how: "标记为已完成——每天一件善行。",
        },
        "social.addFriend": {
            title: "新朋友",
            how: "成为好友后获得。",
        },
        "social.joinClub": {
            title: "加入俱乐部",
            how: "仅一次——加入俱乐部时。",
        },
        "social.firstMessage": {
            title: "第一条消息",
            how: "在每个聊天中发送第一条消息时。",
        },
        "learn.lesson": {
            title: "课程课时",
            how: "完成课时后到账，仅一次。",
        },
        "learn.lessonMinute": {
            title: "课时一分钟",
            how: "课时的每一分钟都计分，完成课时后到账。",
        },
        "learn.course": {
            title: "课时和小测验",
            how: "每个课时和小测验各计一次。",
        },
        "learn.miniTest": {
            title: "小测验",
            how: "每个测验一次。",
        },
        "learn.lessonQuestion": {
            title: "课程提问",
            how: "专家回答后到账。",
        },
        "learn.sup": {
            title: "智能搜索",
            how: "每选择一个问题或每次搜索。",
        },
        "learn.minute": {
            title: "讲座或片段",
            how: "每观看一分钟。",
        },
        "learn.specialist": {
            title: "向专家咨询",
            how: "提交咨询后到账。",
        },
        "learn.appLearning": {
            title: "指南或自我诊断",
            how: "在 MY REVIVE 应用中完成指南或自我诊断。",
        },
    },
};

const AR: PointsActionTexts = {
    all: (s) => `كل النقاط: ${s}`,
    close: "فهمت",
    unit: (n) => arPlural(Math.abs(Math.round(n)), { few: "نقاط", other: "نقطة" }),
    upTo: (a) => `حتى ${a}`,
    about: {
        contrib: "مساهمتك في المشروع: المشتريات والمراجعات وتصحيحات الترجمة النصية.",
        workouts: "التمارين وفق خطتك في تطبيق MY REVIVE.",
        social: "اللقاءات والنادي والأعمال الطيبة.",
        referrals: "الأصدقاء الذين جاؤوا عبر رابطك.",
        learning: "دروس الدورات والاختبارات القصيرة والبحث الذكي.",
    },
    actions: {
        "ref.purchase": {
            title: {
                part: "يشتري صديقك هذا الجزء",
                course: "يشتري صديقك هذه الدورة",
                generic: "يشتري صديق جزءًا من دورة",
                app: "يشتري صديق سنة من التطبيق",
                bundle: "يشتري صديق باقة دورات",
            },
            how: "تصلك حين يدفع صديقك عبر رابطك.",
            benefit: `ولك أيضًا ${B}% من مشترياته في رصيدك.`,
            note: "إن استردّ المبلغ، تُسحب النقاط.",
        },
        "ref.giftClaim": {
            title: "يستلم صديقك الهدية",
            how: "تصلك حين يستلم صديقك هديتك.",
        },
        "ref.install": {
            title: "يثبّت صديق التطبيق",
            how: "تصلك حين يثبّت صديق MY REVIVE عبر رابطك.",
        },
        "ref.started": {
            title: "يبدأ صديق الممارسة",
            how: "تصلك حين يبدأ صديقك التمرّن في MY REVIVE.",
        },
        "ref.newAtMeeting": {
            title: "شخص جديد في لقاء",
            how: "تصلك حين يأتي شخص إلى لقاء لأول مرة ويظهر في صورته.",
        },
        "contrib.purchase": {
            title: {
                generic: "شراء",
                part: "شراء هذا الجزء",
                anyPart: "شراء جزء من دورة",
                course: "شراء دورة",
                app: "سنة من تطبيق MY REVIVE",
                tokens: "باقة رموز",
                plan: "باقة البحث والأسئلة",
                diagnostic: "تشخيص",
                bundle: "باقة دورات",
            },
            how: "تصلك فور الدفع.",
            note: "الاسترداد يسحبها.",
        },
        "contrib.gift": {
            title: "هدية لصديق",
            how: "النقاط لك، كما لو اشتريت لنفسك.",
            benefit: "حين يستلم صديقك الهدية تحصل أيضًا على نقاط، كما عن شراء صديق.",
            note: "الاسترداد يسحبها.",
        },
        "contrib.review": {
            title: "مراجعة منتج",
            how: "تصلك حين ترسل مراجعة — بأي تقييم.",
            note: "مرة واحدة لكل منتج.",
        },
        "contrib.correction": {
            title: "تصحيح الترجمة النصية",
            how: "تصلك بعد التحقق من التصحيح.",
        },
        "contrib.errorReport": {
            title: "بلاغ عن خطأ",
            how: "تصلك بعد أن يؤكد المشرف الخطأ.",
        },
        "contrib.testWorkout": {
            title: "تمرين تجريبي",
            how: "تصلك بعد التحقق من التمرين.",
        },
        "work.weekOnPlan": {
            title: "أسبوع وفق الخطة",
            how: "تُوزّع على تمارين الأسبوع: كلما اقتربت من الخطة زادت.",
        },
        "work.firstPlan": {
            title: "خطتك الأولى",
            how: "مرة واحدة — عن أول خطة تمارين لك.",
        },
        "work.newbie": {
            title: "مبتدئ على الخطة الكاملة",
            how: "إضافة عن كل أسبوع تُنجزه كاملًا على الخطة الكاملة.",
        },
        "social.meeting": {
            title: { generic: "لقاء" },
            how: "لكل من يظهر في الصورة المقبولة للقاء.",
        },
        "social.organizer": {
            title: "لمنظِّم اللقاء",
            how: "عن كل من يحضر لقاءك — فوق نقاط اللقاء نفسه.",
        },
        "social.goodDeed": {
            title: "عمل طيب",
            how: "ضع علامة أنه أُنجز — عمل طيب واحد في اليوم.",
        },
        "social.addFriend": {
            title: "صديق جديد",
            how: "حين تصبحان صديقين.",
        },
        "social.joinClub": {
            title: "الانضمام إلى نادٍ",
            how: "مرة واحدة — حين تنضم إلى نادٍ.",
        },
        "social.firstMessage": {
            title: "الرسالة الأولى",
            how: "عن رسالتك الأولى في كل محادثة.",
        },
        "learn.lesson": {
            title: "درس من الدورة",
            how: "تصلك حين تُنهي الدرس — مرة واحدة لكل درس.",
        },
        "learn.lessonMinute": {
            title: "دقيقة من الدرس",
            how: "عن كل دقيقة من الدرس. تصلك حين تُنهي الدرس.",
        },
        "learn.course": {
            title: "الدروس والاختبارات القصيرة",
            how: "كل درس واختبار قصير يُحتسب مرة واحدة.",
        },
        "learn.miniTest": {
            title: "اختبار قصير",
            how: "مرة واحدة لكل اختبار.",
        },
        "learn.lessonQuestion": {
            title: "سؤال عن الدرس",
            how: "تصلك حين يجيب المختص.",
        },
        "learn.sup": {
            title: "البحث الذكي",
            how: "عن كل سؤال تختاره أو بحث تجريه.",
        },
        "learn.minute": {
            title: "محاضرة أو مقطع",
            how: "عن كل دقيقة تشاهدها.",
        },
        "learn.specialist": {
            title: "طلب إلى مختص",
            how: "تصلك حين ترسل الطلب.",
        },
        "learn.appLearning": {
            title: "دليل أو تشخيص ذاتي",
            how: "عن دليل أو تشخيص ذاتي في تطبيق MY REVIVE.",
        },
    },
};

export type PointsTextLang = "ru" | "en" | "de" | "es" | "cn" | "ar";

const BY_LANG: Record<PointsTextLang, PointsActionTexts> = { ru: RU, en: EN, de: DE, es: ES, cn: CN, ar: AR };

/**
 * Язык текстов окна: ru, en, de, es, cn; zh → cn. Арабский — только при включённом
 * арабском интерфейсе (рубильник AR_UI_ENABLED или предпросмотр, shared/config/arabicUi);
 * иначе /ar, как весь сайт там, — en. Остальные — en.
 */
export const pointsTextLang = (lang: string | null | undefined): PointsTextLang => {
    const l = (lang || "").slice(0, 2).toLowerCase();
    if (l === "zh") return "cn";
    if (l === "ar") return isArabicUi(l) ? "ar" : "en";
    return l === "ru" || l === "de" || l === "es" || l === "cn" ? l : "en";
};

export const pointsActionTexts = (lang: string | null | undefined): PointsActionTexts =>
    BY_LANG[pointsTextLang(lang)];

/** Имя сферы с большой буквы — «Приглашённые», «Invited», «邀请». */
export const sphereTitle = (sphere: SphereKey, lang: string | null | undefined): string => {
    const s = pointsTexts(pointsTextLang(lang)).sphere[sphere];
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
};

/** Заголовок действия: по ctx (часть / курс / вид встречи), иначе общий. */
export const pointsActionTitle = (action: PointsActionKey, ctx: string | undefined, lang: string | null | undefined): string => {
    const l = pointsTextLang(lang);
    if (action === "social.meeting" && isMeetingKind(ctx)) return pointsTexts(l).hint.meeting[ctx];
    const title = BY_LANG[l].actions[action].title;
    if (typeof title === "string") return title;
    return (ctx && title[ctx]) || title.generic || Object.values(title)[0];
};

/** Последнее целое в строке суммы («до +4 450» → 4450) — для «баллов» после неё. */
const lastNumber = (s: string): number | null => {
    const m = s.replace(/[\s  ,.]/g, "").match(/(\d+)(?!.*\d)/);
    return m ? Number(m[1]) : null;
};

/** Действия, чьё число — «до»: всё, что можно набрать. */
const UP_TO = new Set<PointsActionKey>(["learn.course"]);

export type PointsInfoContent = {
    sphere: SphereKey;
    /** «Приглашённые» — строка над заголовком, цветом сферы. */
    kicker: string;
    title: string;
    /** «+18 500» — ровно как на чипе; нет — без числа. */
    amount: string | null;
    /** «баллов» после числа. */
    unit: string | null;
    /** Как и когда придут, выгода, пометка — по строке. */
    lines: string[];
    /** «Все баллы: Приглашённые». */
    all: string;
    close: string;
};

/**
 * Что показать в окне по нажатому чипу. Действия нет или оно незнакомо — как раньше:
 * подпись чипа и фраза о сфере.
 */
export const pointsInfoContent = (
    chip: { sphere: SphereKey; action?: string | null; ctx?: string | null; amount?: string | null; label?: string },
    lang: string | null | undefined,
    formatAmount: (n: number) => string,
): PointsInfoContent => {
    const tx = pointsActionTexts(lang);
    const ctx = chip.ctx || undefined;
    if (!isPointsActionKey(chip.action)) {
        return {
            sphere: chip.sphere,
            kicker: sphereTitle(chip.sphere, lang),
            title: chip.label || sphereTitle(chip.sphere, lang),
            amount: null,
            unit: null,
            lines: [tx.about[chip.sphere]],
            all: tx.all(sphereTitle(chip.sphere, lang)),
            close: tx.close,
        };
    }
    const action = chip.action;
    const sphere = POINTS_ACTION_SPHERE[action];
    const fallback = pointsActionAmount(action, ctx);
    const raw = chip.amount?.trim() || (fallback != null ? formatAmount(fallback) : null);
    // Уроки и мини-тесты — «до +N», как на чипе: столько, если пройти всё.
    const amount = raw && UP_TO.has(action) && /^\+/.test(raw) ? tx.upTo(raw) : raw;
    const n = amount ? lastNumber(amount) : null;
    const text = tx.actions[action];
    return {
        sphere,
        kicker: sphereTitle(sphere, lang),
        title: pointsActionTitle(action, ctx, lang),
        amount,
        unit: n != null ? tx.unit(n) : null,
        lines: [text.how, text.benefit, text.note].filter((x): x is string => Boolean(x)),
        all: tx.all(sphereTitle(sphere, lang)),
        close: tx.close,
    };
};
